import React, { useMemo, useState } from "react";
import { Button, Card, Col, Row, Space, Typography, message } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";

import TransactionReceiptFilters from "../../components/transactionReceipt/TransactionReceiptFilters";
import TransactionReceiptTable from "../../components/transactionReceipt/TransactionReceiptTable";
import TransactionReceiptDocumentModal from "../../components/transactionReceipt/TransactionReceiptDocumentModal";
import TransactionReceiptAddModal from "../../components/transactionReceipt/TransactionReceiptAddModal";
import TransactionReceiptEditModal from "../../components/transactionReceipt/TransactionReceiptEditModal";
import { getTransactionReceiptAccess } from "../../components/transactionReceipt/transactionReceiptPermissions";
import BulkDeleteBar from "../../components/common/BulkDeleteBar";
import useTransactionReceiptList from "../../hooks/useTransactionReceiptList";
import useTableSelection from "../../hooks/useTableSelection";
import {
    bulkDeleteTransactionReceipts,
    deleteTransactionReceipt,
    replaceTransactionReceiptFile,
    uploadTransactionReceiptFile,
} from "../../api/transactionReceiptApi";
import { useTranslation } from "../../i18n/LanguageProvider";

const { Title } = Typography;

/**
 * Гүйлгээний Баримт page: the module's single destination. Listing, searching, filtering,
 * sorting, the PDF receipts and - for the MANAGE roles - Excel import and manual entry all live
 * here.
 *
 * A VIEW role (AUTHOR, CUSTOMER) gets the list and the PDF viewer only: no add button, no
 * upload, replace, edit or delete control is rendered, and the backend refuses those calls
 * anyway.
 */
function TransactionReceiptListPage() {
    const { t } = useTranslation();
    const access = useMemo(() => getTransactionReceiptAccess(), []);

    const {
        rows,
        loading,
        error,
        page,
        setPage,
        pageSize,
        total,
        search,
        setSearch,
        columnFilters,
        executionDateRange,
        setExecutionDateRange,
        filterOptions,
        handleTableChange,
        clearFilters,
        reload,
    } = useTransactionReceiptList();

    // Ticking rows is only offered to the roles that may actually delete them.
    const selection = useTableSelection();
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const [receiptRecord, setReceiptRecord] = useState(null);
    const [receiptModalOpen, setReceiptModalOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);

    const openReceiptModal = (record) => {
        setReceiptRecord(record);
        setReceiptModalOpen(true);
    };

    const closeReceiptModal = () => {
        setReceiptModalOpen(false);
        setReceiptRecord(null);
    };

    const handleUploadReceipt = async (record, file) => {
        if (!access.canUploadReceipt) {
            message.error(t("receipt.noPermission"));
            return;
        }

        const action = record.hasReceipt
            ? replaceTransactionReceiptFile
            : uploadTransactionReceiptFile;

        await action(record.id, file);

        message.success(
            record.hasReceipt ? t("receipt.replaced.ok") : t("receipt.uploaded.ok")
        );
        reload();
    };

    const openEditModal = (record) => {
        setEditRecord(record);
        setEditModalOpen(true);
    };

    const closeEditModal = () => {
        setEditModalOpen(false);
        setEditRecord(null);
    };

    const handleDelete = async (record) => {
        if (!access.canDelete) {
            message.error(t("delete.noPermission"));
            return;
        }

        try {
            await deleteTransactionReceipt(record.id);
            message.success(t("receipt.delete.done"));
            reload();
        } catch (err) {
            message.error(err?.message || t("receipt.delete.failed"));
        }
    };

    const selectedWithReceipt = selection.selectedRows.filter((row) => row?.hasReceipt).length;

    const handleBulkDelete = async () => {
        if (!access.canDelete || !selection.selectedCount) return;

        setBulkDeleting(true);

        try {
            const result = await bulkDeleteTransactionReceipts(selection.selectedRowKeys);

            const deleted = Number(result?.deletedCount ?? 0);
            const missing = Number(result?.missingIds?.length ?? 0);

            message.success(
                missing
                    ? `${t("bulk.deleted")}: ${deleted}. ${t("bulk.alreadyGone")}: ${missing}`
                    : `${t("bulk.deleted")}: ${deleted}`
            );

            selection.clear();

            // Deleting the whole of a later page would otherwise leave the user on an empty one.
            if (deleted >= rows.length && page > 1) setPage(1);

            reload();
        } catch (err) {
            message.error(err?.message || t("bulk.deleteFailed"));
        } finally {
            setBulkDeleting(false);
        }
    };

    const pagination = {
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
        showTotal: (count, range) => `${range[0]}-${range[1]} / ${count}`,
    };

    return (
        <div className="space-y-4">
            <Card bordered={false} className="shadow-sm rounded-2xl">
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                    <Col xs={24} md={14}>
                        <Title level={2} style={{ margin: 0 }}>
                            {t("receipt.title")}
                        </Title>
                    </Col>

                    <Col xs={24} md="auto">
                        <Space wrap>
                            {access.canAdd && (
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setAddModalOpen(true)}
                                >
                                    {t("action.add")}
                                </Button>
                            )}

                            <Button icon={<ReloadOutlined />} onClick={reload} loading={loading}>
                                {t("action.refresh")}
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} xl={8}>
                    <Card bordered={false} className="shadow-sm rounded-2xl">
                        <div>
                            <strong>{t("receipt.stat.total")}:</strong> {filterOptions.totalCount}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} xl={8}>
                    <Card bordered={false} className="shadow-sm rounded-2xl">
                        <div>
                            <strong>{t("receipt.stat.withReceipt")}:</strong>{" "}
                            {filterOptions.withReceiptCount}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} xl={8}>
                    <Card bordered={false} className="shadow-sm rounded-2xl">
                        <div>
                            <strong>{t("receipt.stat.filtered")}:</strong> {total}
                        </div>
                    </Card>
                </Col>
            </Row>

            <TransactionReceiptFilters
                search={search}
                setSearch={setSearch}
                executionDateRange={executionDateRange}
                setExecutionDateRange={setExecutionDateRange}
                columnFilterCount={Object.keys(columnFilters || {}).length}
                totalRows={total}
                onClear={clearFilters}
            />

            <Card bordered={false} className="shadow-sm rounded-2xl">
                <BulkDeleteBar
                    selectedCount={selection.selectedCount}
                    attachmentCount={selectedWithReceipt}
                    loading={bulkDeleting}
                    labels={{
                        selected: t("bulk.selected"),
                        attachments: t("bulk.withReceipt"),
                        clear: t("bulk.clear"),
                        delete: t("action.delete"),
                        confirmTitle: t("bulk.confirmTitle"),
                        confirmBody: t("bulk.receipt.confirmBody"),
                        confirmOk: t("action.delete"),
                        confirmCancel: t("action.cancel"),
                    }}
                    onClear={selection.clear}
                    onDelete={handleBulkDelete}
                />

                <TransactionReceiptTable
                    rows={rows}
                    loading={loading}
                    error={error}
                    pagination={pagination}
                    onChange={handleTableChange}
                    canUploadReceipt={access.canUploadReceipt}
                    currencyOptions={filterOptions.currencies}
                    institutionOptions={filterOptions.institutions}
                    activeFilters={columnFilters}
                    canEdit={access.canEdit}
                    canDelete={access.canDelete}
                    rowSelection={access.canDelete ? selection.rowSelection : undefined}
                    onViewReceipt={openReceiptModal}
                    onUploadReceipt={handleUploadReceipt}
                    onEdit={openEditModal}
                    onDelete={handleDelete}
                    onReload={reload}
                />
            </Card>

            <TransactionReceiptDocumentModal
                open={receiptModalOpen}
                record={receiptRecord}
                canDelete={access.canDelete}
                onClose={closeReceiptModal}
                onDeleted={reload}
            />

            {access.canAdd && (
                <TransactionReceiptAddModal
                    open={addModalOpen}
                    onClose={() => setAddModalOpen(false)}
                    onSaved={reload}
                />
            )}

            {access.canEdit && (
                <TransactionReceiptEditModal
                    open={editModalOpen}
                    record={editRecord}
                    onClose={closeEditModal}
                    onSaved={reload}
                />
            )}
        </div>
    );
}

export default TransactionReceiptListPage;
